import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, X } from "lucide-react";

interface ToastProps {
    message: string;
    type?: "success" | "error";
    onClose: () => void;
    duration?: number;
    position?: "top" | "bottom";
    mode?: "fixed" | "absolute";
}

export default function Toast({ message, type = "success", onClose, duration = 3500, position = "bottom", mode = "fixed" }: ToastProps) {
    useEffect(() => {
        const timer = setTimeout(onClose, duration);
        return () => clearTimeout(timer);
    }, [onClose, duration]);

    const positionClass =
        mode === "absolute"
            ? "absolute top-3 inset-x-0 mx-auto w-fit max-w-[calc(100%-2rem)]"
            : `fixed ${position === "top" ? "top-6" : "bottom-6"} left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-auto`;

    const slideY = mode === "absolute" || position === "top" ? -16 : 16;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: slideY, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: slideY, scale: 0.97 }}
                transition={{ type: "spring", damping: 24, stiffness: 300 }}
                className={`${positionClass} z-[99999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl text-sm font-bold max-w-[calc(100%-2rem)] whitespace-normal break-words ${
                    type === "success"
                        ? "bg-emerald-600 text-white"
                        : "bg-red-600 text-white"
                }`}
            >
                {type === "success" ? (
                    <CheckCircle className="w-4 h-4 shrink-0" />
                ) : (
                    <XCircle className="w-4 h-4 shrink-0" />
                )}
                <span>{message}</span>
                <button
                    onClick={onClose}
                    className="ml-2 opacity-70 hover:opacity-100 transition"
                    aria-label="Dismiss"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </motion.div>
        </AnimatePresence>
    );
}
